import { useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  getChatControlData,
  setChatDialogOpen,
  setVideoChatOpen,
} from 'components/ChatDialog/store/slices/chatSlice';
import { getVideoChatControlData } from 'components/ChatDialog/store/slices/videoChatSlice';
import { setThreadWithUsers } from 'components/ChatDialog/store/thunks/loadThreadUsers';
import { loadVideoChatUsers } from 'components/ChatDialog/store/thunks/loadVideoChatUsers';
import {
  showChatNotification,
  showVideoCallNotification,
} from 'utils/browserNotifications';

/**
 * Custom hook to handle chat and video call notifications
 * Listens to notifyMessage from Redux and displays browser notifications
 */
export const useChatNotifications = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const { t } = useTranslation('common');

  const { notifyMessage } = useSelector(getChatControlData);

  // notifyMessage outlives its notification: only opening the chat or switching
  // thread clears it. While this effect also depended on the threads map, every
  // thread refetch (each socket reconnect, so at least every two hours) showed
  // the same stale message again. Each message notifies once, by id; the same
  // id arriving again (a retried push, a call re-broadcast by each joiner) too.
  const notifiedRef = useRef(null);

  useEffect(() => {
    if (!notifyMessage) return;

    const notificationKey = notifyMessage.id || notifyMessage;
    if (notifiedRef.current === notificationKey) return;
    notifiedRef.current = notificationKey;

    // Only try to show notification if permission is already granted
    if (Notification.permission === 'granted') {
      const messageType = notifyMessage.type.split('#')[0];

      // Read at click time, not at notification time: the thread may only
      // have loaded since.
      const threadOfMessage = () =>
        getChatControlData(store.getState()).threads[notifyMessage.threadId];

      if (messageType === 'MESSAGE') {
        showChatNotification(notifyMessage, () => {
          // On notification click, open the chat thread
          const thread = threadOfMessage();
          if (!thread) return;
          dispatch(setThreadWithUsers({ thread }));
          dispatch(setChatDialogOpen(true));
        });
      } else if (messageType === 'VIDEOCHAT_SESSION') {
        // A focused tab shows the call in IncomingCallBanner; a system
        // notification on top of it would only repeat it.
        if (document.hasFocus()) return;

        const sender = getVideoChatControlData(store.getState()).users[
          notifyMessage.userId
        ];

        const handleVideoCallNotification = (senderName) => {
          showVideoCallNotification(
            `${senderName} ${t('chat.is_calling', 'is calling')}`,
            () => {
              const thread = threadOfMessage();
              if (!thread) return;
              dispatch(setThreadWithUsers({ thread }));
              dispatch(setChatDialogOpen(true));
              dispatch(setVideoChatOpen(true));
            }
          );
        };

        if (sender && sender.firstName) {
          handleVideoCallNotification(sender.firstName);
        } else {
          dispatch(loadVideoChatUsers([notifyMessage.userId])).then(
            (result) => {
              handleVideoCallNotification(
                result.payload?.[notifyMessage.userId]?.firstName || ''
              );
            }
          );
        }
      }
    }
  }, [notifyMessage, dispatch, store, t]);
};
